import { useCallback } from "react";
import { Formik, Form, FormikProps, FormikHelpers } from "formik";
import { Grid, Button, ButtonGroup, Typography, Box } from "@mui/material";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import AmplifyAPIWrapper, { INotifications } from "src/API/AmplifyAPIWrapper";
import { IActiveModal } from "src/pages/Admin/AdminWindow";
import { FormikTextField } from "src/components/MUI5FormikComponents/FormikWrappers";
import { FormikColorPickField } from "src/components/MUI5FormikComponents/FormikWrappers";
import { IDispatchDeskExpanded } from "src/redux/reducers/DispatchDeskReducer";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface IDispatchDeskFormProps {
  selectedDesk: IDispatchDeskExpanded | undefined;
  updateDeskList: (desks: IDispatchDeskExpanded[], mode: "create" | "update" | "delete", deskId?: string) => void;
  setActiveModal: (activeModal: IActiveModal | null) => void;
  setSelectedDesk: (desk: IDispatchDeskExpanded | undefined) => void;
  nextDisplayOrder: number;
}

interface IDispatchDeskFormValues {
  _id?: string;
  name: string;
  color: string;
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

const DispatchDeskSchema = Yup.object().shape({
  name: Yup.string().required("Name is required"),
  color: Yup.string().nullable(),
});

// Expanded color palette for dispatch desks
const DESK_COLORS = [
  // Blues
  "#1E3A5F",
  "#2364AA",
  "#3B82F6",
  "#60A5FA",
  "#93C5FD",
  // Greens
  "#065F46",
  "#29BF12",
  "#10B981",
  "#34D399",
  "#6EE7B7",
  // Reds / Pinks
  "#9B1B30",
  "#F21B3F",
  "#EF4444",
  "#F87171",
  "#FF88DC",
  // Oranges / Yellows
  "#B45309",
  "#FF9914",
  "#F59E0B",
  "#FBBF24",
  "#FDE68A",
  // Purples
  "#5B21B6",
  "#7C3AED",
  "#8B5CF6",
  "#A78BFA",
  "#C4B5FD",
  // Teals / Cyans
  "#0E7490",
  "#08BDBD",
  "#06B6D4",
  "#22D3EE",
  "#CAE5FF",
  // Neutrals
  "#374151",
  "#6B7280",
  "#9CA3AF",
  "#D1D5DB",
  "#F3F4F6",
];

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

const defaultValues: IDispatchDeskFormValues = {
  _id: undefined,
  name: "",
  color: "",
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const DispatchDeskForm = (props: IDispatchDeskFormProps): JSX.Element => {
  const dispatch = useDispatch();
  const { selectedDesk, updateDeskList, setActiveModal, setSelectedDesk, nextDisplayOrder } = props;

  const initialValues: IDispatchDeskFormValues = selectedDesk
    ? {
        _id: selectedDesk._id,
        name: selectedDesk.name,
        color: selectedDesk.color ?? "",
      }
    : defaultValues;

  // ------ Submit (Create / Update) ------

  const handleSubmit = useCallback(
    async (values: IDispatchDeskFormValues, helpers: FormikHelpers<IDispatchDeskFormValues>): Promise<void> => {
      const { setSubmitting, setStatus } = helpers;
      const mode = values._id ? "update" : "create";

      const body: Record<string, any> = { name: values.name };
      if (values.color) body.color = values.color;
      // For new desks, auto-assign them to the end of the list
      if (mode === "create") {
        body.displayOrder = nextDisplayOrder;
      }

      const notifications: INotifications = {
        dispatch,
        infoMessage: `${mode === "update" ? "Updating" : "Creating"} Dispatch Desk…`,
        successMessage: `Dispatch Desk ${mode === "update" ? "Updated" : "Created"}.`,
        errorMessage: `Error: Unable to ${mode === "update" ? "update" : "create"} Dispatch Desk.`,
      };

      try {
        const response =
          mode === "create"
            ? await AmplifyAPIWrapper.Post<any>("/admin/dispatchdesk", body, notifications)
            : await AmplifyAPIWrapper.Put<any>(`/admin/dispatchdesk/${values._id}`, body, notifications);

        if (Array.isArray(response)) {
          updateDeskList(response as IDispatchDeskExpanded[], mode, values._id);
          setStatus({ success: true });
        } else {
          setStatus({ success: false });
        }
      } catch {
        setStatus({ success: false });
      }
      setSubmitting(false);
    },
    [dispatch, updateDeskList, nextDisplayOrder]
  );

  // ------ Delete ------

  const handleDelete = useCallback(
    (formik: FormikProps<IDispatchDeskFormValues>): void => {
      if (!selectedDesk?._id) return;

      setActiveModal({
        title: "Delete Dispatch Desk",
        message:
          "Are you sure you want to delete this dispatch desk? All flight and user associations will be removed. This cannot be undone.",
        cancel: () => setActiveModal(null),
        confirm: async (): Promise<void> => {
          setActiveModal(null);
          const notifications: INotifications = {
            dispatch,
            infoMessage: "Deleting Dispatch Desk…",
            successMessage: "Dispatch Desk Deleted.",
            errorMessage: "Error: Unable to delete Dispatch Desk.",
          };

          const response = await AmplifyAPIWrapper.Del<any>(
            `/admin/dispatchdesk/${selectedDesk._id}`,
            {},
            notifications
          );

          if (Array.isArray(response)) {
            updateDeskList(response as IDispatchDeskExpanded[], "delete", selectedDesk._id);
            formik.resetForm({ values: defaultValues });
          } else if (!response?.error) {
            // If the response is not the expected array, still try to clear
            updateDeskList([], "delete", selectedDesk._id);
            formik.resetForm({ values: defaultValues });
          }
          formik.setSubmitting(false);
        },
        confirmLabel: "Delete",
      });
    },
    [dispatch, selectedDesk, setActiveModal, updateDeskList]
  );

  // ------ Render ------

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize={true}
      onSubmit={handleSubmit}
      validationSchema={DispatchDeskSchema}
    >
      {(formik: FormikProps<IDispatchDeskFormValues>) => {
        const { dirty, isValid, isSubmitting, handleReset } = formik;

        return (
          <Form>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {/* Desk Name */}
              <Grid item xs={12}>
                <FormikTextField label="Name" name="name" />
              </Grid>

              {/* Color */}
              <Grid item xs={12}>
                <FormikColorPickField label="Color" name="color" colors={DESK_COLORS} pickerWidth="287px" />
              </Grid>

              {/* Desk preview */}
              {formik.values.name && (
                <Grid item xs={12}>
                  <Typography variant="caption" sx={{ mb: 0.5, display: "block", opacity: 0.7 }}>
                    Preview
                  </Typography>
                  <Box
                    sx={{
                      borderTop: `4px solid ${formik.values.color || "#666"}`,
                      borderRadius: 1,
                      p: 1.5,
                      bgcolor: "background.paper",
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Typography variant="subtitle2">{formik.values.name}</Typography>
                  </Box>
                </Grid>
              )}

              {/* Actions */}
              <Grid item xs={12}>
                <ButtonGroup variant="contained">
                  <Button color="success" disabled={isSubmitting || !isValid || !dirty} type="submit">
                    {selectedDesk ? "Update" : "Create"}
                  </Button>
                  <Button
                    type="reset"
                    color="secondary"
                    disabled={(!dirty && isValid) || isSubmitting}
                    onClick={handleReset}
                  >
                    Reset
                  </Button>
                  {selectedDesk && (
                    <Button color="error" disabled={!selectedDesk || isSubmitting} onClick={() => handleDelete(formik)}>
                      Delete
                    </Button>
                  )}
                </ButtonGroup>
              </Grid>
            </Grid>
          </Form>
        );
      }}
    </Formik>
  );
};
